import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';
import { AlertCircle, Loader2 } from 'lucide-react';

/**
 * EMZI NEXUS — SSO Callback handler.
 *
 * Path: /sso/callback
 *
 * The backend `/api/sso/nexus` 302-redirects browsers here with our newly
 * minted EMZI session tokens in query params. We:
 *   1. Persist tokens to localStorage via secureStorage
 *   2. Fetch the user profile via /api/auth/session (or fallback to /api/auth/me)
 *   3. Hydrate AuthContext.login(...)
 *   4. Stash `return_to` so Logout can send the user back to Nexus Hub
 *   5. Redirect to /dashboard
 */

const SSOCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState('');
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const returnTo = params.get('return_to');

    if (!accessToken) {
      setError('Missing access_token in SSO callback URL.');
      return;
    }

    // Persist tokens immediately so /api/auth/me succeeds
    secureStorage.setItem('nexus_access_token', accessToken);
    if (refreshToken) {
      secureStorage.setItem('nexus_refresh_token', refreshToken);
    }
    if (returnTo) {
      try {
        sessionStorage.setItem('nexus_sso_return_to', returnTo);
      } catch (_e) {
        // ignore (private mode)
      }
    }

    // Hydrate the user profile
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/session`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          throw new Error(`Profile fetch failed: HTTP ${res.status}`);
        }
        const profile = await res.json();
        // AuthContext.login persists user & tokens
        login({
          access_token: accessToken,
          refresh_token: refreshToken || undefined,
          user: profile.user || profile,
        });
        navigate('/dashboard', { replace: true });
      } catch (e) {
        setError(`Could not complete SSO login: ${e.message}`);
      }
    })();
  }, []);

  if (error) {
    return (
      <div
        data-testid="sso-callback-error"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#fff',
        }}
      >
        <div
          style={{
            maxWidth: 460,
            border: '1px solid #FCA5A5',
            background: '#FEF2F2',
            borderRadius: 12,
            padding: 24,
            color: '#991B1B',
            textAlign: 'center',
          }}
        >
          <AlertCircle size={32} style={{ margin: '0 auto 8px' }} />
          <h2 style={{ margin: '0 0 8px 0', fontSize: 18 }}>SSO Login Failed</h2>
          <p style={{ margin: '0 0 16px 0', fontSize: 14 }}>{error}</p>
          <button
            data-testid="sso-callback-retry-btn"
            onClick={() => navigate('/login', { replace: true })}
            style={{
              background: '#DC2626',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="sso-callback-loading"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 12,
        background: '#fff',
        color: '#0e67a2',
      }}
    >
      <Loader2 size={36} style={{ animation: 'spin 1s linear infinite' }} />
      <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Completing SSO login…</p>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default SSOCallback;
